import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Download, Mail, Printer } from 'lucide-react';
import TransactionOverlay from '../transactions/TransactionOverlay';
import InvoiceSendModal from '../InvoiceSendModal';
import { getInvoiceById, downloadInvoicePdf } from '../../utils/invoiceApi';
import { printSalesInvoice } from '../../utils/printSalesInvoice';
import type { Invoice, InvoiceStatus } from '../../types/invoice';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';
import { parseInventoryQty } from '../../utils/negativeInventory';
import { canManageInvoices } from '../../utils/salesTransactionPermissions';
import { toast } from '../../utils/toast';

export interface InvoicePreviewModalProps {
  invoiceId: number;
  onClose: () => void;
}

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const getStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const customerName = (invoice: Invoice) =>
  invoice.customer_snapshot.name || invoice.customer?.display_name || '—';

const InvoicePreviewModal = ({ invoiceId, onClose }: InvoicePreviewModalProps) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (Number.isNaN(invoiceId)) {
      setError('Invalid invoice ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setInvoice(await getInvoiceById(invoiceId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    void loadInvoice();
  }, [loadInvoice]);

  const handlePrint = async () => {
    if (!invoice) return;
    try {
      setActionLoading(true);
      await printSalesInvoice(invoice);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      setActionLoading(true);
      await downloadInvoicePdf(invoice.id, `${invoice.invoice_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download invoice PDF');
    } finally {
      setActionLoading(false);
    }
  };

  const lines = invoice?.lines ?? [];
  const hasBackorderLines = lines.some((line) => parseInventoryQty(line.backorder_qty) > 0);

  return (
    <>
    <TransactionOverlay
      variant="preview"
      title={invoice?.invoice_number ?? 'Invoice'}
      subtitle={invoice ? `Invoice · ${formatLabel(invoice.status)}` : 'Loading…'}
      onClose={onClose}
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : invoice ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}
            >
              {formatLabel(invoice.status)}
            </span>
            {invoice.allow_negative_stock && (
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Oversell allowed
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm bg-gray-50 rounded-lg p-4">
            <div>
              <span className="text-gray-500 block text-xs">Customer</span>
              <span className="font-medium">{customerName(invoice)}</span>
            </div>
            {invoice.customer_snapshot.email && (
              <div>
                <span className="text-gray-500 block text-xs">Email</span>
                <span>{invoice.customer_snapshot.email}</span>
              </div>
            )}
            {invoice.customer_snapshot.phone && (
              <div>
                <span className="text-gray-500 block text-xs">Phone</span>
                <span>{invoice.customer_snapshot.phone}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-gray-500 block text-xs">Date</span>
                <span>{formatDate(invoice.txn_date)}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">Due</span>
                <span>{formatDate(invoice.due_date)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-gray-500 block text-xs">Total</span>
                <span className="font-medium">{formatCurrency(invoice.total_amount)}</span>
              </div>
              <div>
                <span className="text-gray-500 block text-xs">Balance</span>
                <span
                  className={
                    parseFloat(invoice.balance) > 0
                      ? 'font-semibold text-red-600'
                      : 'font-semibold text-green-600'
                  }
                >
                  {formatCurrency(invoice.balance)}
                </span>
              </div>
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Terms</span>
              <span>{formatLabel(invoice.payment_terms)}</span>
            </div>
          </div>

          {invoice.memo && (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-700">Memo:</span> {invoice.memo}
            </p>
          )}

          {lines.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Line items</h4>
              {hasBackorderLines && (
                <p className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Includes backordered lines — COGS may update when stock is received.
                </p>
              )}
              <ul className="text-sm space-y-2">
                {lines.map((line) => (
                  <li key={line.id} className="flex justify-between gap-3 bg-gray-50 px-3 py-2 rounded">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{line.item_snapshot.name}</div>
                      {line.item_snapshot.sku?.trim() && (
                        <div className="text-xs text-gray-500 font-mono truncate">
                          {line.item_snapshot.sku}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        {formatPreviewQuantity(line.qty)} × {formatCurrency(line.unit_price)}
                      </div>
                    </div>
                    <span className="font-mono shrink-0">{formatCurrency(line.line_total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => void handlePrint()}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button
              type="button"
              onClick={() => void handleDownloadPdf()}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Download
            </button>
            {canManageInvoices() && (
              <button
                type="button"
                onClick={() => setShowSendModal(true)}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <Mail className="h-4 w-4" />
                Send
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </TransactionOverlay>

    {invoice && (
      <InvoiceSendModal
        invoice={invoice}
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
      />
    )}
    </>
  );
};

export default InvoicePreviewModal;
